import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["inert"]
const _hoisted_2 = ["inert"]
const _hoisted_3 = {
  key: 0,
  class: "error",
  role: "alert"
}
const _hoisted_4 = { class: "workspace-panel" }
const _hoisted_5 = { class: "file-toolbar" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = {
  key: 1,
  class: "empty"
}

import { inject, onBeforeUnmount, onMounted, ref, watch, type Ref } from "vue";
import { toast } from "vue-sonner";
import SectionLayout from "@/components/Layout/SectionLayout.vue";
import StudioHistoryToolbar from "../StudioHistoryToolbar.vue";
import { useStudioDocumentHistory } from "../useStudioHistory";
import { studioEditorActiveKey } from "../studioSessionHistory";
import StudioFileList from "../StudioFileList.vue";
import StudioCreateDialog from "../StudioCreateDialog.vue";
import type { StudioEditorKind } from "../studioSidebar";
import StudioSidebarContent from "../StudioSidebarContent.vue";
import { StorageClass } from "@/services/storage/storage";
import { downloadTextFile } from "@/utils/download";
import { ProjectID } from "../../constant/constant";
import { createWorkspaceSaveQueue } from "../QuestEditor/workspaceSaveQueue";
import WalkTalkPanel from "./WalkTalkPanel.vue";
import { useWorkspaceStructIds } from "../useWorkspaceStructIds";
import { createWalkTalkProject, decodeWalkTalkProject, encodeWalkTalkProject, type WalkTalkProject } from "./walkTalkProject";
import { exportWalkTalk } from "./walkTalkExporter";
import RuntimeImportButton from "../RuntimeImportButton.vue";
import { commitRuntimeImport } from "../runtimeImportStorage";
import { importWalkTalk } from "./walkTalkImporter";


export default /*@__PURE__*/_defineComponent({
  __name: 'WalkTalkEditor',
  props: {
    editorKind: { default: "WalkTalk" }
  },
  emits: ["update:editorKind"],
  setup(__props: any, { expose: __expose, emit: __emit }) {



const emit = __emit;
const storage = inject<StorageClass>("storage")!;
const editorActive = inject(studioEditorActiveKey, () => true);
const workspace = inject<Ref<string>>("selectedWorkspaceId")!;
const directory = `/${workspace.value}/WalkTalkEditor`;
const project = ref<WalkTalkProject>();
const files = ref<string[]>([]);
const selectedFile = ref("");
const creating = ref(false);
const newName = ref("");
const busy = ref(false);
const saveStatus = ref("未打开文件");
const fileError = ref("");
const workspaceIds = useWorkspaceStructIds();
let disposed = false;
let loading = false;
let requestId = 0;
let listRequestId = 0;
let revision = 0;
const historyElement = ref<HTMLElement>();
const history = useStudioDocumentHistory({
  project, element: historyElement, blocked: () => busy.value, label: "修改边走边说",
  onError: error => { console.error(error); toast.error((error instanceof Error ? error.message : String(error))); },
});

function showError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error);
  console.error(error); toast.error(message);
  return message;
}
const saveQueue = createWorkspaceSaveQueue(async (path, data) => {
  const version = revision;
  await storage.setProject(ProjectID).writeFile(path, data);
  if (!disposed && version === revision) saveStatus.value = "已自动保存";
}, error => { saveStatus.value = "保存失败"; showError(error); });
__expose({ prepareToLeave: () => {
  if (busy.value) return Promise.reject(new Error("边走边说文件正在读写，请稍后切换"));
  return saveQueue.flush();
} });
watch(project, () => {
  if (disposed || loading || !project.value || !selectedFile.value) return;
  revision++;
  saveStatus.value = "待保存…";
  saveQueue.schedule(`${directory}/${selectedFile.value}`, encodeWalkTalkProject(project.value));
}, { deep: true, flush: "sync" });

async function refreshFiles() {
  const request = ++listRequestId;
  try {
    const exists = await storage.setProject(ProjectID).exists(directory);
    if (disposed || request !== listRequestId) return;
    const result = exists ? await storage.setProject(ProjectID).getFiles(directory) : [];
    if (!disposed && request === listRequestId) {
      files.value = result.filter(name => /^[^/\\]+\.json$/i.test(name)).sort();
      fileError.value = "";
    }
  } catch (error) { if (!disposed && request === listRequestId) fileError.value = showError(error); }
}
async function selectFile(name: string, fromCreate = false) {
  if (disposed || (busy.value && !fromCreate) || !/^[^/\\]+\.json$/i.test(name)) return;
  const request = ++requestId;
  busy.value = true;
  try {
    await saveQueue.flush();
    if (disposed || request !== requestId) return;
    const loaded = decodeWalkTalkProject(await storage.setProject(ProjectID).readFile(`${directory}/${name}`));
    if (disposed || request !== requestId) return;
    loading = true;
    selectedFile.value = name;
    project.value = loaded;
    loading = false;

    fileError.value = "";
    saveStatus.value = "已加载";
  } catch (error) { if (!disposed && request === requestId) fileError.value = showError(error); }
  finally { if (!disposed && request === requestId) busy.value = false; }
}
async function createFile() {
  if (busy.value || disposed) return;
  const base = newName.value.trim().replace(/\.json$/i, "");
  if (!base || /[<>:"/\\|?*\u0000-\u001f]/.test(base) || base === "." || base === "..") { toast.warning("请输入有效文件名，不能包含路径或特殊字符"); return; }
  const name = `${base}.json`;
  busy.value = true;
  try {
    const exists = await storage.setProject(ProjectID).exists(`${directory}/${name}`);
    if (disposed) return;
    if (exists) { toast.warning("已有同名文件"); return; }
    await saveQueue.flush();
    if (disposed) return;
    await storage.setProject(ProjectID).writeFile(`${directory}/${name}`, encodeWalkTalkProject(createWalkTalkProject()));
    if (disposed) return;
    await refreshFiles();
    creating.value = false; newName.value = "";
    if (!disposed) await selectFile(name, true);
  } catch (error) { if (!disposed) fileError.value = showError(error); }
  finally { if (!disposed) busy.value = false; }
}
async function deleteFile() {
  if (busy.value || disposed) return;
  const name = selectedFile.value;
  if (!name) { toast.warning("请先选择文件"); return; }
  if (!confirm(`将边走边说文件「${name}」移入回收站？其中所有台词会一并移入。`)) return;
  busy.value = true;
  try {
    await saveQueue.flush();
    if (disposed) return;
    await storage.setProject(ProjectID).trash(`${directory}/${name}`);
    saveQueue.discard(`${directory}/${name}`);
    if (disposed) return;
    requestId++;
    loading = true; project.value = undefined; selectedFile.value = ""; loading = false;
    saveStatus.value = "未打开文件";
    await refreshFiles();
    if (!disposed) toast.success("边走边说文件已移入回收站");
  } catch (error) { if (!disposed) fileError.value = showError(error); }
  finally { if (!disposed) busy.value = false; }
}
async function importConfiguration(file: File) {
  if (busy.value || disposed) return;
  busy.value = true;
  try {
    const result = await commitRuntimeImport({ file, decode: importWalkTalk, encode: encodeWalkTalkProject,
      storage: storage.setProject(ProjectID), active: () => !disposed, flush: () => saveQueue.flush(), directory });
    if (!result) return;
    loading = true; selectedFile.value = result.name; project.value = result.project; loading = false;
    creating.value = false; fileError.value = ""; saveStatus.value = "已导入";
    await refreshFiles();
    if (!disposed) toast.success(`已新增「${result.name}」`);
  } finally { loading = false; if (!disposed) busy.value = false; }
}
function exportVariables() {
  if (busy.value || !project.value || !selectedFile.value) return;
  try {
    const result = exportWalkTalk(workspaceIds.walkTalk(project.value));
    downloadTextFile(result.json, `${selectedFile.value.replace(/\.json$/i, "")}-边走边说.json`, "application/json");
    toast.success(`已按当前顺序导出 ${project.value.entries.length} 条台词`);
  } catch (error) { showError(error); }
}
async function saveShortcut(event: KeyboardEvent) {
  if (!editorActive()) return;
  if (event.repeat || (!event.ctrlKey && !event.metaKey) || event.key.toLowerCase() !== "s") return;
  event.preventDefault();
  if (busy.value || disposed || !project.value || !selectedFile.value) return;
  // 保存队列会显示失败状态并提示错误，不再下载编辑器文件。
  await saveQueue.flush().catch(() => undefined);
}
onMounted(() => { void refreshFiles(); window.addEventListener("keydown", saveShortcut); });
onBeforeUnmount(() => {
  disposed = true; requestId++; listRequestId++;
  void saveQueue.flush().catch(() => undefined);
  window.removeEventListener("keydown", saveShortcut);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "historyElement",
    ref: historyElement,
    class: "walk-talk-editor",
    inert: busy.value
  }, [
    _createVNode(StudioSidebarContent, null, {
      default: _withCtx(() => [
        _createElementVNode("div", {
          class: "file-panel",
          inert: busy.value
        }, [
          _createVNode(StudioFileList, {
            disabled: busy.value,
            values: files.value,
            "selected-value": selectedFile.value,
            onSelect: selectFile,
            onAdd: _cache[0] || (_cache[0] = ($event: any) => (creating.value = true)),
            onDelete: deleteFile
          }, {
            actions: _withCtx(() => [
              _createVNode(RuntimeImportButton, {
                disabled: busy.value,
                "import-file": importConfiguration
              }, null, 8, ["disabled"])
            ]),
            _: 1
          }, 8, ["disabled", "values", "selected-value"]),
          _cache[4] || (_cache[4] = _createElementVNode("p", { class: "file-help" }, "每个文件是一段顺序台词，保存在当前工作区。", -1)),
          (fileError.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_3, [
                _createTextVNode(_toDisplayString(fileError.value), 1),
                _createElementVNode("button", {
                  type: "button",
                  onClick: refreshFiles
                }, "刷新列表")
              ]))
            : _createCommentVNode("", true)
        ], 8, _hoisted_2)
      ]),
      _: 1
    }),
    _createVNode(SectionLayout, {
      title: selectedFile.value || '边走边说',
      class: "walk-talk-edit-section"
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("header", _hoisted_5, [
            _createElementVNode("span", null, _toDisplayString(selectedFile.value || '未选择文件'), 1),
            _createElementVNode("small", null, _toDisplayString(saveStatus.value), 1),
            _createVNode(StudioHistoryToolbar, {
              history: _unref(history),
              disabled: busy.value
            }, null, 8, ["history", "disabled"]),
            (selectedFile.value && project.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "button",
                  class: "primary",
                  disabled: busy.value,
                  onClick: exportVariables
                }, "导出千星边走边说", 8, _hoisted_6))
              : _createCommentVNode("", true)
          ]),
          (project.value)
            ? (_openBlock(), _createBlock(WalkTalkPanel, {
                key: selectedFile.value,
                project: project.value
              }, null, 8, ["project"]))
            : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _cache[5] || (_cache[5] = _createElementVNode("h3", null, "一段按顺序播放的台词", -1)),
                _cache[6] || (_cache[6] = _createElementVNode("p", null, "从左侧选择文件，或创建一份新的边走边说列表。", -1)),
                _createElementVNode("button", {
                  type: "button",
                  class: "primary",
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (creating.value = true))
                }, "＋ 新建边走边说")
              ]))
        ])
      ]),
      _: 1
    }, 8, ["title"]),
    (creating.value)
      ? (_openBlock(), _createBlock(StudioCreateDialog, {
          key: 0,
          modelValue: newName.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((newName).value = $event)),
          title: "新建边走边说文件",
          label: "边走边说文件名",
          placeholder: "输入文件名",
          "submit-label": "创建并打开",
          busy: busy.value,
          onSubmit: createFile,
          onClose: _cache[3] || (_cache[3] = ($event: any) => (creating.value = false))
        }, null, 8, ["modelValue", "busy"]))
      : _createCommentVNode("", true)
  ], 8, _hoisted_1))
}
}

})